import React from 'react';
import { AlertTriangle, CircleCheck } from 'lucide-react';

/**
 * The wizard footer's validation hint, shared by the envelope builder and the
 * template editor.
 *
 * Extracted because both footers held an identical copy, and identical markup in
 * two files is how the stepper's off-centre numbers got fixed in one wizard and
 * stayed broken in the other.
 *
 * Sits immediately LEFT of the primary button rather than centred in the row:
 * its whole job is to say why that button is disabled, so it belongs beside the
 * thing it explains.
 *
 * `max-w` plus `truncate` is load-bearing, not tidiness. The longest message the
 * validator produces is 72 characters ("The signed-document file column no
 * longer exists or is not a File column"), and at this size that would push the
 * button off the edge of a narrow item view. The full text stays reachable in
 * the `title`.
 */
export default function FooterHint({ hint }) {
  if (!hint) return null;

  const warn = hint.tone === 'warn';
  const Icon = warn ? AlertTriangle : CircleCheck;
  const full = hint.prefix ? `${hint.prefix}: ${hint.text}` : hint.text;

  return (
    <div
      title={full}
      className={`flex items-center gap-2 min-w-0 max-w-[26rem] px-3 py-1.5 rounded-r-md border-l-2 ${
        warn
          ? 'bg-amber-50 border-amber-600 text-amber-900'
          : 'bg-green-50 border-green-600 text-green-900'
      }`}
    >
      <Icon size={13} className='flex-shrink-0' />
      <span className='text-xs truncate'>
        {/* The step is the actionable half when the failure is on an earlier
            step, so it carries the weight rather than hiding inside the run of
            text. */}
        {hint.prefix && <span className='font-medium'>{hint.prefix}: </span>}
        {hint.text}
      </span>
    </div>
  );
}
